import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import Drawer from 'shared/components/drawer'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useToggle } from 'shared/hooks/use-toggle'
import CapIcon from 'shared/icons/cap-icon'
import CloseIcon from 'shared/icons/close-icon'
import { CourseSideMenu } from '../course-side-menu'

interface MobileCourseSideMenuProps {
  className?: string
}

export default function MobileCourseSideMenu({ className }: MobileCourseSideMenuProps) {
  const { t } = useTranslation()
  const [isOpenModuleList, toggle, setIsOpenModuleList] = useToggle()
  const buttonRef = useRef(null)
  const drawerRef = useRef(null)

  let Icon = CapIcon
  let label = 'course.show_curriculum'

  if (isOpenModuleList) {
    Icon = CloseIcon
    label = 'course.hide_curriculum'
  }

  const handleClickOutside = useCallback(() => setIsOpenModuleList(false), [setIsOpenModuleList])
  useClickOutside(handleClickOutside, null, [buttonRef.current, drawerRef.current])

  useEffect(() => {
    if (!isOpenModuleList) return

    const handleBlur = () => {
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') {
          setIsOpenModuleList(false)
        }
      }, 0)
    }

    window.addEventListener('blur', handleBlur)
    return () => window.removeEventListener('blur', handleBlur)
  }, [isOpenModuleList, setIsOpenModuleList])

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggle}
        className={twMerge(
          'flex size-16 items-center justify-center rounded-full bg-background-secondary p-3 shadow-[0_2px_20px_0_rgba(0,0,0,0.1)]',
          className,
        )}
        aria-label={t(label)}
      >
        <Icon
          className={twJoin(
            'pointer-events-none',
            isOpenModuleList ? 'size-6 text-text-primary' : 'text-brand-primary',
          )}
        />
      </button>
      <Drawer
        ref={drawerRef}
        controlled
        open={isOpenModuleList}
        className="bg-THEME-block-background"
      >
        <CourseSideMenu menuClassName="px-0" />
      </Drawer>
    </>
  )
}
